<template>
  <form @submit.prevent>
    <div class="pb-6">
      <VSelect
        v-model="data.status"
        :options="statusOptions"
        :reduce="(option) => option.value"
        :disabled="!canEdit || !isIdle"
        class="vue-select w-48"
      />
    </div>
    <div class="space-y-12">
      <Attributes
        :editable="canEdit && isIdle"
        :name.sync="data.name"
        :requirements.sync="data.requirements"
        :status.sync="data.status"
      />

      <RelatedNotes
        :editable="canEdit && isIdle"
        :notes="data.notes"
        @update:notes="data.notes = $event"
      />

      <RelatedLinks
        :editable="canEdit && isIdle"
        :links="data.links"
        @update:links="data.links = $event"
      />

      <RelatedAttachments
        v-if="id"
        :production-order-item-id="id"
        :editable="canEdit && isIdle"
        :attachments="data.media"
        @update:attachments="data.media = $event"
      />

      <div class="flex items-center space-x-2">
        <button
          class="px-4 py-2 leading-5 text-sm rounded border border-transparent text-white focus:outline-none"
          :class="[
            {
              'bg-green-600': !isRejected,
              'hover:bg-green-500': !isRejected,
              'focus:border-green-700': !isRejected,
              'focus:ring ring-green-500 ring-opacity-50': !isRejected,
            },
            {
              'bg-red-600': isRejected,
              'hover:bg-red-500': isRejected,
              'focus:border-red-700': isRejected,
              'focus:ring ring-green-500 ring-opacity-50': isRejected,
            },
          ]"
          @click="update"
        >
          <template v-if="isResolved"> Update succeed! </template>
          <template v-else-if="isRejected"> Update failed! </template>
          <template v-else> Update </template>
        </button>
        <Loader v-if="isPending" small />
      </div>
    </div>
  </form>
</template>

<script>
import Attributes from './productionOrderItem/Attributes.vue';
import RelatedNotes from './productionOrderItem/RelatedNotes.vue';
import RelatedLinks from './productionOrderItem/RelatedLinks.vue';
import RelatedAttachments from './productionOrderItem/RelatedAttachments.vue';

const defaultProductionOrderItemData = {
  uuid: null,
  name: '',
  requirements: null,
  status: null,
  notes: [],
  links: [],
  media: [],
};

const STATUS_IDLE = 'STATUS_IDLE';
const STATUS_PENDING = 'STATUS_PENDING';
const STATUS_RESOLVED = 'STATUS_RESOLVED';
const STATUS_REJECTED = 'STATUS_REJECTED';

export default {
  name: 'ProductionOrderItem',

  components: {
    Attributes,
    RelatedNotes,
    RelatedLinks,
    RelatedAttachments,
  },

  props: {
    id: { type: String, default: null },
  },

  data() {
    return {
      canEdit: true,
      statusOptions: [
        { value: 'draft', label: 'Draft' },
        { value: 'pending', label: 'Pending' },
        { value: 'in progress', label: 'In progress' },
        { value: 'in review', label: 'In review' },
        { value: 'completed', label: 'Completed' },
      ],
      status: STATUS_IDLE,
      data: {
        ...defaultProductionOrderItemData,
      },
    };
  },

  computed: {
    productionOrderItem() {
      if (!this.id) {
        return undefined;
      }

      return this.$store.state.entities.orderItems.byId[this.id];
    },
    isIdle() {
      return this.status === STATUS_IDLE;
    },
    isPending() {
      return this.status === STATUS_PENDING;
    },
    isResolved() {
      return this.status === STATUS_RESOLVED;
    },
    isRejected() {
      return this.status === STATUS_REJECTED;
    },
  },

  watch: {
    productionOrderItem: 'setData',
  },

  mounted() {
    if (this.productionOrderItem) {
      this.setData();
    }
  },

  methods: {
    setData() {
      this.data.uuid = this.productionOrderItem.uuid;
      this.data.name = this.productionOrderItem.name;
      this.data.requirements = this.productionOrderItem.requirements;
      this.data.status = this.productionOrderItem.status;
      this.data.links = this.productionOrderItem.links.map(
        (id) => this.$store.state.entities.links.byId[id]
      );
      this.data.notes = this.productionOrderItem.notes.map(
        (id) => this.$store.state.entities.notes.byId[id]
      );
      this.data.media = this.productionOrderItem.media.map(
        (id) => this.$store.state.entities.media.byId[id]
      );
    },
    async update() {
      if (this.status === STATUS_PENDING) {
        return;
      }

      this.status = STATUS_PENDING;

      try {
        await this.$store.dispatch({
          type: 'ui/orderItems/update',
          id: this.id,
          body: this.data,
        });

        this.status = STATUS_RESOLVED;
      } catch (error) {
        // should handle 244 errors
        this.status = STATUS_REJECTED;
      } finally {
        setTimeout(() => {
          this.status = STATUS_IDLE;
        }, 1000);
      }
    },
  },
};
</script>
